import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { FiCheck, FiX, FiEye, FiClock, FiArrowLeft } from 'react-icons/fi';
import axios from 'axios';
import BaseUrl from '../../../Url/BaseUrl';
// API base URL

// Pending Item Component
const PendingItem = ({ item, type, onApprove, onReject, onViewDetails }) => {
  const getItemDetails = () => {
    switch(type) {
      case 'busRoutes':
        return {
          title: item.routeName,
          subtitle: `Route No: ${item.routeNo}`,
          status: item.status ? 'Approved' : 'Pending',
          iconColor: '#667eea'
        };
      case 'routePaths':
        return {
          title: item.routePathName,
          subtitle: `Path No: ${item.routePathNo} | Route: ${item.routeNo}`,
          status: item.status ? 'Approved' : 'Pending',
          iconColor: '#f093fb'
        };
      case 'routePathVillages':
        return {
          title: `City/Village: ${item.cityVillageCode}`,
          subtitle: `Path No: ${item.routePathNo} | Route Village No: ${item.routeVillagesPathNo}`,
          status: item.status ? 'Approved' : 'Pending',
          iconColor: '#4CAF50'
        };
      default:
        return {};
    }
  };

  const details = getItemDetails();

  return (
    <div className="pending-item">
      <div className="pending-item__header">
        <div 
          className="pending-item__icon"
          style={{ backgroundColor: details.iconColor }}
        >
          <FiClock size="1.2em" />
        </div>
        <div className="pending-item__info">
          <h4 className="pending-item__title">{details.title}</h4>
          <p className="pending-item__subtitle">{details.subtitle}</p>
        </div>
        <span className={`pending-item__status ${item.status ? 'approved' : 'pending'}`}>
          {details.status}
        </span>
      </div>
      
      <div className="pending-item__actions">
        <button 
          className="btn-action btn-view"
          onClick={() => onViewDetails(item, type)}
        >
          <FiEye /> View Details
        </button>
        <button 
          className="btn-action btn-approve"
          onClick={() => onApprove(item, type)}
          disabled={item.status}
        >
          <FiCheck /> {item.status ? 'Approved' : 'Approve'}
        </button>
        <button 
          className="btn-action btn-reject"
          onClick={() => onReject(item, type)}
          disabled={item.status}
        >
          <FiX /> Reject
        </button>
      </div>
    </div>
  );
};

// Details Modal Component
const DetailsModal = ({ item, type, isOpen, onClose }) => {
  if (!isOpen) return null;

  const renderDetails = () => {
    return Object.entries(item).map(([key, value]) => (
      <div key={key} className="detail-row">
        <span className="detail-label">{key}:</span>
        <span className="detail-value">
          {value === null ? 'N/A' : value.toString()}
        </span>
      </div>
    ));
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h3>Item Details</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="details-grid">
            {renderDetails()}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Main RouteApprovals Component
const RouteApprovals = ({ onBack }) => {
  const [pendingItems, setPendingItems] = useState({
    busRoutes: [],
    routePaths: [],
    routePathVillages: []
  });
  const [loading, setLoading] = useState({
    busRoutes: true,
    routePaths: true,
    routePathVillages: true
  });
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('busRoutes');

  const { user } = useSelector((state) => state.auth);

  // Fetch pending items
  const fetchPendingItems = async () => {
    try {
      // Fetch bus routes
      const busRoutesResponse = await axios.get(`${BaseUrl.baseurl}BusRoutes/GetPending`);
      setPendingItems(prev => ({ ...prev, busRoutes: busRoutesResponse.data }));
      setLoading(prev => ({ ...prev, busRoutes: false }));
    } catch (error) {
      console.error('Error fetching bus routes:', error);
      setLoading(prev => ({ ...prev, busRoutes: false }));
    }

    try {
      // Fetch route paths
      const routePathsResponse = await axios.get(`${BaseUrl.baseurl}RoutePaths/GetPending`);
      setPendingItems(prev => ({ ...prev, routePaths: routePathsResponse.data }));
      setLoading(prev => ({ ...prev, routePaths: false }));
    } catch (error) {
      console.error('Error fetching route paths:', error);
      setLoading(prev => ({ ...prev, routePaths: false }));
    }

    try {
      // Fetch route path villages
      const routePathVillagesResponse = await axios.get(`${BaseUrl.baseurl}RoutePathVillages/GetPending`);
      setPendingItems(prev => ({ 
        ...prev, 
        routePathVillages: routePathVillagesResponse.data 
      }));
      setLoading(prev => ({ ...prev, routePathVillages: false }));
    } catch (error) {
      console.error('Error fetching route path villages:', error);
      setLoading(prev => ({ ...prev, routePathVillages: false }));
    }
  };

  useEffect(() => {
    fetchPendingItems();
  }, []);

  // Handle approval
  const handleApprove = async (item, type) => {
    try {
      let endpoint = '';
      let payload = { ...item, status: true };

      switch(type) {
        case 'busRoutes':
          endpoint = `${BaseUrl.baseurl}BusRoutes/${item.routeNo}`;
          break;
        case 'routePaths':
          endpoint = `${BaseUrl.baseurl}RoutePaths/${item.routePathNo}`;
          break;
        case 'routePathVillages':
          endpoint = `${BaseUrl.baseurl}RoutePathVillages/${item.routeVillagesPathNo}`;
          break;
        default:
          return;
      }

      await axios.put(endpoint, payload);
      
      // Update local state
      setPendingItems(prev => ({
        ...prev,
        [type]: prev[type].map(i => 
          (type === 'busRoutes' && i.routeNo === item.routeNo) ||
          (type === 'routePaths' && i.routePathNo === item.routePathNo) ||
          (type === 'routePathVillages' && i.routeVillagesPathNo === item.routeVillagesPathNo)
            ? { ...i, status: true }
            : i
        )
      }));

      alert('Item approved successfully!');
    } catch (error) {
      console.error('Error approving item:', error);
      alert('Failed to approve item. Please try again.');
    }
  };

  // Handle rejection
  const handleReject = async (item, type) => {
    if (!window.confirm('Are you sure you want to reject this item?')) return;

    try {
      let endpoint = '';
      
      switch(type) {
        case 'busRoutes':
          endpoint = `${BaseUrl.baseurl}BusRoutes/${item.routeNo}`;
          break;
        case 'routePaths':
          endpoint = `${BaseUrl.baseurl}RoutePaths/${item.routePathNo}`;
          break;
        case 'routePathVillages':
          endpoint = `${BaseUrl.baseurl}RoutePathVillages/${item.routeVillagesPathNo}`;
          break;
        default:
          return;
      }

      await axios.delete(endpoint);
      
      // Remove from local state
      setPendingItems(prev => ({
        ...prev,
        [type]: prev[type].filter(i => 
          !((type === 'busRoutes' && i.routeNo === item.routeNo) ||
            (type === 'routePaths' && i.routePathNo === item.routePathNo) ||
            (type === 'routePathVillages' && i.routeVillagesPathNo === item.routeVillagesPathNo))
        )
      }));

      alert('Item rejected and deleted successfully!');
    } catch (error) {
      console.error('Error rejecting item:', error);
      alert('Failed to reject item. Please try again.');
    }
  };

  // Handle view details
  const handleViewDetails = (item, type) => {
    setSelectedItem(item);
    setSelectedType(type);
    setIsModalOpen(true);
  };

  // Refresh all data
  const handleRefresh = () => {
    setLoading({
      busRoutes: true,
      routePaths: true,
      routePathVillages: true
    });
    fetchPendingItems();
  };

  // Tab configuration
  const tabs = [
    { id: 'busRoutes', label: 'Pending Bus Routes', count: pendingItems.busRoutes.length },
    { id: 'routePaths', label: 'Pending Route Paths', count: pendingItems.routePaths.length },
    { id: 'routePathVillages', label: 'Pending Route Villages', count: pendingItems.routePathVillages.length }
  ];

  // Render loading skeleton
  const renderLoadingSkeleton = () => {
    return Array(3).fill(0).map((_, index) => (
      <div key={index} className="pending-item loading">
        <div className="pending-item__header">
          <div className="pending-item__icon skeleton"></div>
          <div className="pending-item__info">
            <div className="skeleton-text" style={{ width: '200px' }}></div>
            <div className="skeleton-text" style={{ width: '150px' }}></div>
          </div>
          <div className="skeleton-text" style={{ width: '80px' }}></div>
        </div>
        <div className="pending-item__actions">
          <div className="skeleton-button" style={{ width: '120px' }}></div>
          <div className="skeleton-button" style={{ width: '100px' }}></div>
          <div className="skeleton-button" style={{ width: '100px' }}></div>
        </div>
      </div>
    ));
  };

  return (
    <div className="route-approvals">
      <div className="route-approvals__header">
        <button className="btn-back" onClick={onBack}>
          <FiArrowLeft /> Back to Routes Management
        </button>
        <div className="route-approvals__title-section">
          <h1>Route Approvals Management</h1>
          <p>Review and approve pending bus routes, route paths, and village mappings</p>
        </div>
        <div className="route-approvals__actions">
          <button className="btn-refresh" onClick={handleRefresh}>
            Refresh All
          </button>
        </div>
      </div>

      {user.role === 'superadmin' && (
        <div className="admin-badge">
          <span className="admin-badge__text">Super Admin View</span>
          <span className="admin-badge__dot"></span>
        </div>
      )}

      <div className="approvals-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
            {tab.count > 0 && <span className="tab-count">{tab.count}</span>}
          </button>
        ))}
      </div>

      <div className="approvals-content">
        <div className="pending-items-list">
          {loading[activeTab] ? (
            renderLoadingSkeleton()
          ) : pendingItems[activeTab].length === 0 ? (
            <div className="empty-state">
              <div className="empty-state__icon">📋</div>
              <h3>No Pending Items</h3>
              <p>All {tabs.find(t => t.id === activeTab)?.label.toLowerCase()} have been processed.</p>
            </div>
          ) : (
            pendingItems[activeTab].map((item, index) => (
              <PendingItem
                key={index}
                item={item}
                type={activeTab}
                onApprove={handleApprove}
                onReject={handleReject}
                onViewDetails={handleViewDetails}
              />
            ))
          )}
        </div>

        <div className="approvals-summary">
          <div className="summary-card">
            <h3>Approvals Summary</h3>
            <div className="summary-stats">
              <div className="stat-item">
                <span className="stat-label">Pending Bus Routes</span>
                <span className="stat-value">{pendingItems.busRoutes.filter(r => !r.status).length}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Pending Route Paths</span>
                <span className="stat-value">{pendingItems.routePaths.filter(r => !r.status).length}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Pending Village Mappings</span>
                <span className="stat-value">{pendingItems.routePathVillages.filter(r => !r.status).length}</span>
              </div>
              <div className="stat-item total">
                <span className="stat-label">Total Pending</span>
                <span className="stat-value">
                  {pendingItems.busRoutes.filter(r => !r.status).length +
                   pendingItems.routePaths.filter(r => !r.status).length +
                   pendingItems.routePathVillages.filter(r => !r.status).length}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <DetailsModal
        item={selectedItem}
        type={selectedType}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
};

export default RouteApprovals;